import React from "react";

import { DropdownItem } from "@common/components/Dropdown/Dropdown";
import { Spinner } from "@common/components/Spinner/Spinner";
import { usePopover } from "@common/hooks/filtering/ui/SimplePopover";
import { vars } from "@common/styles";
import { Flex } from "@common/styles/reusable/Flex/Flex.styles";
import { FloatingOverlay } from "@floating-ui/react";
import * as Styled from "@main/pages/shared/components/BasicsPopoverSearch/BasicsPopoverSearch.styles";

interface BasicsItemPopoverProps {
  query: string;
  setQuery: (query: string) => void;
  error: boolean;
  loading: boolean;
  results: string[] | undefined;
  onValueSelected: (value: string) => void;
  popover: Pick<ReturnType<typeof usePopover>, "floating" | "getFloatingProps" | "x" | "y" | "strategy">;
}

export function BasicsPopoverSearch({
  query,
  setQuery,
  error,
  loading,
  results = [],
  onValueSelected,
  popover,
}: BasicsItemPopoverProps) {
  const handleKeydownEvent = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") onValueSelected(event.currentTarget.value);
    event.stopPropagation();
  };

  return (
    <FloatingOverlay
      style={{
        zIndex: vars.zIndex.modalOverlay,
        width: "100vw",
        overflow: "inherit",
      }}
    >
      <Styled.BasicsItemPopover
        ref={popover.floating}
        {...popover.getFloatingProps()}
        style={{
          top: popover.y ?? 0,
          left: popover.x ?? 0,
          width: "20.8rem",
          minHeight: "10rem",
          position: popover.strategy,
          zIndex: vars.zIndex.modalContent,
        }}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: "0.2rem" }}>
          <Styled.Input
            autoFocus
            placeholder="Search"
            maxLength={1000}
            onChange={(e: React.FormEvent<HTMLInputElement>) => setQuery(e.currentTarget.value)}
            value={query}
            type="text"
            onKeyDown={handleKeydownEvent}
          />
          <Styled.HitsContainer $column>
            {query ? (
              <DropdownItem onClick={() => onValueSelected(query)}>
                <Styled.Label>{`Enter '${query}'`}</Styled.Label>
              </DropdownItem>
            ) : null}
            <Styled.Helper p3>{"Or choose from results"}</Styled.Helper>
            <Flex $column>
              {loading && (
                <div style={{ margin: "1.4rem 0rem" }}>
                  <Spinner style={{ width: "2.2rem", height: "2.2rem" }} />
                </div>
              )}
              {error && <Styled.ResultText $isError={error}>{"Error loading results"}</Styled.ResultText>}
              {!loading && !error && results.length == 0 && <Styled.ResultText>{"No results found"}</Styled.ResultText>}
              {!loading &&
                !error &&
                results.length > 0 &&
                results.map((value: any, index: any) => {
                  return (
                    <DropdownItem
                      onClick={() => onValueSelected(value)}
                      key={index}
                    >
                      <Styled.Label>{value}</Styled.Label>
                    </DropdownItem>
                  );
                })}
            </Flex>
          </Styled.HitsContainer>
          <Styled.ClearContainer $column>
            <DropdownItem
              error={true}
              onClick={() => onValueSelected("")}
            >
              <Styled.Label>{"Clear"}</Styled.Label>
            </DropdownItem>
          </Styled.ClearContainer>
        </div>
      </Styled.BasicsItemPopover>
    </FloatingOverlay>
  );
}
